/**
 * Publitas Cart Integration
 * Public API for external integrations to interact with the Huntington Store cart
 * 
 * This allows Publitas (giftguide.huntington.org) to refresh the cart display
 * after adding items via Shopify's cart API
 */

window.HuntingtonCart = window.HuntingtonCart || {};

/**
 * Refresh the cart display (ajax cart drawer/mini-cart and count)
 * Call this after adding items to cart via Shopify's /cart/add.js API
 * 
 * @returns {boolean} - Returns true if cart was refreshed, false if ajax cart not available
 * 
 * @example
 * // After adding to cart:
 * if (window.HuntingtonCart && window.HuntingtonCart.refreshCart) {
 *   window.HuntingtonCart.refreshCart();
 * }
 */
window.HuntingtonCart.refreshCart = function() {
  console.log('[Huntington Cart] Refreshing cart...');
  
  // Check if the ajax cart module exists
  if (window.PXUTheme && window.PXUTheme.jsAjaxCart && typeof window.PXUTheme.jsAjaxCart.updateView === 'function') {
    window.PXUTheme.jsAjaxCart.updateView();
    console.log('[Huntington Cart] Cart refreshed successfully');
    return true;
  }
  
  console.warn('[Huntington Cart] Ajax cart not available, falling back to cart count update');
  
  // Fallback: fetch and update cart count directly
  window.HuntingtonCart.getCartCount().then(function(count) {
    window.HuntingtonCart.updateCartCount(count);
  });
  
  return false;
};

/**
 * Get current cart item count from Shopify
 * 
 * @returns {Promise<number>} - Promise that resolves with the cart item count
 * 
 * @example
 * window.HuntingtonCart.getCartCount().then(count => {
 *   console.log('Current cart has', count, 'items');
 * });
 */
window.HuntingtonCart.getCartCount = function() {
  return fetch('/cart.js', {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
  .then(function(response) {
    if (!response.ok) {
      throw new Error('Failed to fetch cart: ' + response.statusText);
    }
    return response.json();
  })
  .then(function(cart) {
    console.log('[Huntington Cart] Cart count:', cart.item_count);
    return cart.item_count;
  })
  .catch(function(error) {
    console.error('[Huntington Cart] Error fetching cart count:', error);
    return 0;
  });
};

/**
 * Directly update cart count indicator in the UI
 * This is faster than refreshCart() but doesn't update the cart contents
 * Use this if you just need to update the number quickly
 * 
 * @param {number} [count] - The cart item count to display. If not provided, will fetch from Shopify
 * @returns {Promise|undefined} - Returns a Promise if fetching count, undefined if count was provided
 * 
 * @example
 * // With a known count:
 * window.HuntingtonCart.updateCartCount(5);
 * 
 * // Auto-fetch the count:
 * window.HuntingtonCart.updateCartCount().then(() => console.log('Updated!'));
 */
window.HuntingtonCart.updateCartCount = function(count) {
  // If no count provided, fetch it from Shopify
  if (typeof count === 'undefined' || count === null) {
    console.log('[Huntington Cart] No count provided, fetching from Shopify...');
    return window.HuntingtonCart.getCartCount().then(function(fetchedCount) {
      window.HuntingtonCart.updateCartCount(fetchedCount);
      return fetchedCount;
    });
  }
  
  console.log('[Huntington Cart] Updating cart count to:', count);
  
  // Update all elements with data-bind="itemCount"
  var countElements = document.querySelectorAll('[data-bind="itemCount"]');
  countElements.forEach(function(el) {
    el.textContent = count;
  });
  
  // Update the cart button text in header
  var cartButtons = document.querySelectorAll('.cart-button__text');
  cartButtons.forEach(function(button) {
    button.textContent = 'CART (' + count + ')';
  });
  
  // Add/remove the has-cart-count class on cart triggers
  var cartTriggers = document.querySelectorAll('[data-ajax-cart-trigger]');
  cartTriggers.forEach(function(trigger) {
    if (count > 0) {
      trigger.classList.add('has-cart-count');
    } else {
      trigger.classList.remove('has-cart-count');
    }
  });
};

/**
 * Get full cart data from Shopify
 * 
 * @returns {Promise<Object>} - Promise that resolves with the full cart object
 * 
 * @example
 * window.HuntingtonCart.getCart().then(cart => {
 *   console.log('Cart total:', cart.total_price);
 *   console.log('Cart items:', cart.items);
 * });
 */
window.HuntingtonCart.getCart = function() {
  return fetch('/cart.js', {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
  .then(function(response) {
    if (!response.ok) {
      throw new Error('Failed to fetch cart: ' + response.statusText);
    }
    return response.json();
  })
  .catch(function(error) {
    console.error('[Huntington Cart] Error fetching cart:', error);
    throw error;
  });
};

/**
 * Show the cart drawer (if using drawer cart action)
 * 
 * @example
 * window.HuntingtonCart.showCart();
 */
window.HuntingtonCart.showCart = function() {
  if (window.PXUTheme && window.PXUTheme.jsAjaxCart && typeof window.PXUTheme.jsAjaxCart.showDrawer === 'function') {
    window.PXUTheme.jsAjaxCart.showDrawer();
    console.log('[Huntington Cart] Cart drawer opened');
    return true;
  }
  console.warn('[Huntington Cart] Cart drawer not available');
  return false;
};

// Log that the integration is loaded
console.log('[Huntington Cart] Publitas integration loaded successfully');
console.log('[Huntington Cart] Available methods:', Object.keys(window.HuntingtonCart));

